import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Info, Building2, Heart, Users, Accessibility, Trophy, Waves, ArrowRight, RefreshCw } from "lucide-react";
import { LaxOrnament, StyvmorsviolOrnament, OrnamentDivider } from "@/components/Ornaments";

import { usePageMeta } from "@/utils/usePageMeta";

export default function OmOss() {
  usePageMeta({
    title: "Om oss – vidalv.live",
    description: "Bakgrunden till vidalv.live – en modererad lokal samlingsplats för Backe, Rossön och bygden vid Fjällsjöälven. Redaktionell policy, namnval och hur sidan drivs.",
    path: "/om-oss",
  });
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div data-testid="om-oss-page" className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <p className="font-body text-[11px] font-semibold text-bark/50 uppercase tracking-[0.22em] mb-1.5">Om oss</p>
      <h1 className="font-fraktur-smooth text-xl sm:text-2xl text-falu tracking-wide mb-3 leading-[1.05]">Om vidalv.live</h1>
      <p className="text-base text-bark mb-8 max-w-2xl leading-relaxed">
        Med bygden menar vi Backe, Rossön och alla byar som binder dem samman &ndash; längs älven, runt sjöarna, genom skogen.
      </p>

      {/* Ångermanland heritage-kort */}
      <section data-testid="angermanland-section" className="relative mb-10 bg-angermanland/5 border border-angermanland/25 rounded-sm p-6 sm:p-8 overflow-hidden">
        <StyvmorsviolOrnament className="absolute -top-2 -right-2 w-20 text-angermanland opacity-20" />
        <StyvmorsviolOrnament className="absolute -bottom-3 -left-3 w-16 text-angermanland opacity-15" />
        <div className="relative flex items-start gap-3">
          <LaxOrnament className="w-24 text-angermanland shrink-0 hidden sm:block" />
          <div className="flex-1">
            <p className="font-body text-[11px] font-semibold text-angermanland uppercase tracking-[0.22em] mb-1">Landskapet Ångermanland</p>
            <h2 className="font-heading text-xl sm:text-2xl font-semibold text-ink mb-2 leading-tight">Tre silverlaxar i Fjällsjöälven</h2>
            <p className="text-sm text-ink/80 leading-relaxed mb-3">
              Fjällsjö socken ligger i Ångermanlands norra landskap. Landskapsvapnet &ndash; tre silverlaxar simmandes i blått &ndash; är nästan bokstavligt: älven är en av Sveriges laxrikaste vattendrag. Landskapsblomman är styvmorsviol, landskapsdjuret bäver, landskapsfågeln stenskvätta. Små, envisa, tåliga.
            </p>
            <OrnamentDivider className="text-angermanland opacity-40 my-2" />
            <p className="text-xs text-bark/70 italic">
              vidalv.live bär dessa symboler som diskret bakgrund &ndash; för att minna oss om var vi är.
            </p>
          </div>
        </div>
      </section>

      <div className="flex flex-col gap-8">
        {/* Namnet - link till berättelsen */}
        <section data-testid="namnet-section" className="bg-pine/5 border border-pine/20 rounded-sm p-6 sm:p-8">
          <div className="flex items-start gap-3">
            <Waves size={20} className="text-pine mt-0.5 shrink-0" />
            <div className="flex-1">
              <h2 className="font-heading text-xl sm:text-2xl font-semibold text-ink mb-2">Varför vidalv.live?</h2>
              <p className="text-sm text-ink/80 leading-relaxed mb-4">
                Om älven som har burit bygden i tre hundra år, sista stocken som flottades 1965, och varför lokal information måste hållas levande för hand.
              </p>
              <Link
                to="/upptack/namnet"
                data-testid="link-namnet-story"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-falu hover:text-falu-hover no-underline transition-colors"
              >
                Läs berättelsen <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>

        {/* Disclaimer */}
        <section data-testid="disclaimer-section" className="bg-parchment border-l-4 border-pine rounded-sm p-6 sm:p-8">
          <div className="flex items-start gap-3">
            <Info size={20} className="text-pine mt-0.5 shrink-0" />
            <div>
              <h2 className="font-heading text-xl sm:text-2xl font-semibold text-ink mb-3">Inte kommun, inte myndighet</h2>
              <p className="text-sm text-ink/80 leading-relaxed">
                vidalv.live är inte kopplat till Strömsunds kommun, Länsstyrelsen eller någon annan myndighet. Vi tar inte emot formella anmälningar, ärenden eller klagomål. Vill du nå kommunen, kontakta <a href="https://www.stromsund.se" target="_blank" rel="noopener noreferrer" className="text-falu hover:text-falu-hover underline">stromsund.se</a> eller ring <a href="tel:067016100" className="text-falu hover:text-falu-hover underline">0670-161 00</a>.
              </p>
            </div>
          </div>
        </section>

        {/* Levande lokal information — Etappe 4.4 positionierungs-/copy-fix */}
        <section data-testid="levande-info-section" className="bg-parchment border border-warm-border rounded-sm p-6 sm:p-8">
          <div className="flex items-start gap-3">
            <RefreshCw size={20} className="text-pine mt-0.5 shrink-0" />
            <div>
              <h2 className="font-heading text-xl sm:text-2xl font-semibold text-ink mb-3">Lokal information som går att hålla levande</h2>
              <p className="text-sm text-ink/80 leading-relaxed mb-3">
                vidalv.live är byggt för att lokal information ska vara lättare att hitta, uppdatera och förbättra över tid. Platser, föreningar, företag, evenemang och praktiska uppgifter kan rättas och kompletteras när något ändras.
              </p>
              <p className="text-sm text-ink/80 leading-relaxed mb-3">
                För att arbetet ska vara tydligt sparas ändringar internt med datum och ansvarig redaktör. Det gör det lättare att följa upp information, rätta fel och samarbeta med fler personer utan att tappa överblicken.
              </p>
              <p className="text-sm text-ink/80 leading-relaxed">
                Målet är enkel nytta för boende, besökare, föreningar, företag och bygden som helhet — utan att allt måste försvinna i sociala medier.
              </p>
            </div>
          </div>
        </section>

        {/* Projektägare */}
        <section data-testid="projekt-section" className="bg-parchment border border-warm-border rounded-sm p-6 sm:p-8">
          <div className="flex items-start gap-3">
            <Building2 size={20} className="text-pine mt-0.5 shrink-0" />
            <div>
              <h2 className="font-heading text-xl sm:text-2xl font-semibold text-ink mb-3">Byggd och underhållen lokalt</h2>
              <p className="text-sm text-ink/80 leading-relaxed mb-3">
                vidalv.live byggs och underhålls av <strong>hodema edertools ab</strong> i Backe &ndash; samma bygd, samma älv.
              </p>
              <p className="text-sm text-ink/80 leading-relaxed mb-3">
                Plattformen är öppen, modererad och kostnadsfri. Vi tjänar inget på dina uppgifter.
              </p>
              <p className="text-sm text-bark/70 leading-relaxed mt-4">
                Om du har en egen idé för något digitalt &ndash; en webbplats, en app, ett verktyg &ndash; går det bra att höra av sig.
                <br /><a href="mailto:info@hodema.se" className="text-falu hover:text-falu-hover no-underline font-semibold">info@hodema.se</a>
              </p>
            </div>
          </div>
        </section>

        {/* Tillgänglighet */}
        <section data-testid="tillganglighet-section" className="bg-parchment border-l-4 border-falu rounded-sm p-6 sm:p-8">
          <div className="flex items-start gap-3">
            <Accessibility size={20} className="text-falu mt-0.5 shrink-0" />
            <div>
              <h2 className="font-heading text-xl sm:text-2xl font-semibold text-ink mb-3">För alla &ndash; oavsett förutsättning</h2>
              <p className="text-sm text-ink/80 leading-relaxed mb-3">
                vidalv.live ska vara tillgängligt för alla. Vi arbetar aktivt för att plattformen ska fungera bra även för personer med funktionsnedsättning.
              </p>
              <ul className="text-sm text-ink/80 leading-relaxed list-disc pl-5 flex flex-col gap-2 mb-3">
                <li><strong>Synnedsättning</strong> &ndash; Tydliga kontraster, stor text, semantisk HTML.</li>
                <li><strong>Hörselnedsättning</strong> &ndash; All information som text.</li>
                <li><strong>Motoriska svårigheter</strong> &ndash; Stora klickytor, enkel navigering.</li>
                <li><strong>Kognitiva svårigheter</strong> &ndash; Tydligt språk, korta texter.</li>
                <li><strong>Äldre utan teknikvana</strong> &ndash; Stor text, logisk layout.</li>
              </ul>
              <p className="text-sm text-ink/80 leading-relaxed mb-3">
                Saknar du något? Hör av dig till <a href="mailto:redaktion@vidalv.live" className="text-falu hover:text-falu-hover underline">redaktion@vidalv.live</a>.
              </p>
              <p className="text-xs text-bark">
                Vi strävar efter att följa <a href="https://www.digg.se/kunskap-och-stod/digital-tillganglighet" target="_blank" rel="noopener noreferrer" className="text-falu hover:text-falu-hover underline">DIGG:s riktlinjer</a> (WCAG 2.1 AA).
              </p>
            </div>
          </div>
        </section>

        {/* Co-Moderator */}
        <section data-testid="moderator-section" className="bg-pine/5 border border-pine/20 rounded-sm p-6 sm:p-8">
          <div className="flex items-start gap-3">
            <Users size={20} className="text-pine mt-0.5 shrink-0" />
            <div>
              <h2 className="font-heading text-xl sm:text-2xl font-semibold text-ink mb-3">Vill du vara med och driva sidan?</h2>
              <p className="text-sm text-ink/80 leading-relaxed mb-3">
                Vi söker <strong>medmoderatorer</strong> &ndash; personer som bor i bygden och vill hålla plattformen levande. Som moderator granskar du inskickade anslag och ser till att tonen är hederlig.
              </p>
              <div className="bg-parchment rounded-sm p-4 mb-3">
                <p className="text-xs font-semibold text-ink mb-2">Som moderator kan du:</p>
                <ul className="text-xs text-bark list-disc pl-5 flex flex-col gap-1">
                  <li>Granska och publicera anslag, bilder och tips</li>
                  <li>Uppdatera information om företag och föreningar</li>
                  <li>Lägga till evenemang och vardagsinformation</li>
                  <li>Vara en lokal kontaktperson</li>
                </ul>
              </div>
              <p className="text-sm text-ink/80 leading-relaxed">
                Intresserad? Skriv till <a href="mailto:redaktion@vidalv.live" className="text-falu hover:text-falu-hover underline">redaktion@vidalv.live</a>. Vi ser gärna moderatorer i <strong>både Backe (Fjällsjö) och Rossön (Bodum)</strong>.
              </p>
            </div>
          </div>
        </section>

        {/* Namnet – avslutad dialog med bygden */}
        <section id="namntavling" data-testid="namnval-section" className="bg-parchment border border-warm-border rounded-sm p-6 sm:p-8">
          <div className="flex items-start gap-3">
            <Trophy size={20} className="text-falu mt-0.5 shrink-0" />
            <div>
              <h2 className="font-heading text-xl sm:text-2xl font-semibold text-ink mb-3">Varifrån kommer namnet?</h2>
              <p className="text-sm text-ink/80 leading-relaxed mb-2">
                Namnet <strong>vidalv.live</strong> valdes efter dialog med bygden våren 2026 &ndash; det syftar
                på liv vid älven, vid <em>Fjällsjöälven</em>, och passar både Backe, Rossön och de mindre byarna runt omkring.
              </p>
              <p className="text-sm text-ink/80 leading-relaxed">
                Tack till alla som bidrog med förslag och tankar.
              </p>
            </div>
          </div>
        </section>

        {/* Vad är det här */}
        <section className="bg-parchment border border-warm-border rounded-sm p-6 sm:p-8">
          <h2 className="font-heading text-xl sm:text-2xl font-semibold text-ink mb-3">Vad är det här?</h2>
          <p className="text-sm text-ink/80 leading-relaxed">
            En modererad anslagstavla och samlingsplats för det som händer, behövs och erbjuds lokalt. Ingen reklam, ingen marknad &ndash; bara bygden.
          </p>
        </section>

        {/* Bygden på nätet */}
        <section className="bg-parchment border border-warm-border rounded-sm p-6 sm:p-8">
          <h2 className="font-heading text-xl sm:text-2xl font-semibold text-ink mb-3">Bygden på nätet</h2>
          <p className="text-sm text-ink/80 leading-relaxed mb-3">
            Utöver den här sidan finns bygden på flera ställen.
          </p>
          <ul className="text-sm text-ink/80 leading-relaxed flex flex-col gap-2">
            <li><a href="https://www.facebook.com/groups/backeross%C3%B6nhotagen" target="_blank" rel="noopener noreferrer" className="text-falu hover:text-falu-hover underline">Backe, Rossön, Hotagen med omnejd</a> &ndash; Facebook-grupp, ca 1 500 medlemmar</li>
            <li><a href="https://www.facebook.com/groups/153492898346492" target="_blank" rel="noopener noreferrer" className="text-falu hover:text-falu-hover underline">Fjällsjö, Rossön med omnejd</a> &ndash; Facebook-grupp, ca 2 000 medlemmar</li>
            <li><a href="http://backe.nu" target="_blank" rel="noopener noreferrer" className="text-falu hover:text-falu-hover underline">backe.nu</a> &ndash; Lokal informationssida</li>
            <li><a href="https://www.stromsund.se/3657.html" target="_blank" rel="noopener noreferrer" className="text-falu hover:text-falu-hover underline">stromsund.se/backe-rossön</a> &ndash; Kommunens sida om bygden</li>
          </ul>
        </section>

        {/* Ansvar och kontakt — Etappe 4.8 */}
        <section data-testid="ansvar-och-kontakt" className="bg-parchment border border-warm-border rounded-sm p-6 sm:p-8">
          <h2 className="font-heading text-xl sm:text-2xl font-semibold text-ink mb-3">Ansvar och kontakt</h2>
          <p className="text-sm text-ink/80 leading-relaxed mb-3">
            vidalv.live drivs lokalt av <strong>Hodema Edertools AB</strong> i Backe. Sidan är inte en kommunal eller statlig myndighet, utan en lokal informationsplattform för Backe, Rossön och bygden runt omkring.
          </p>
          <p className="text-sm text-ink/80 leading-relaxed mb-3">
            Frågor, rättelser eller önskemål om borttagning skickas till{" "}
            <a href="mailto:redaktion@vidalv.live" className="text-falu hover:text-falu-hover">redaktion@vidalv.live</a>.
          </p>
          <ul className="text-sm flex flex-wrap gap-x-5 gap-y-2 text-falu">
            <li><Link to="/integritet" data-testid="ansvar-link-integritet" className="hover:text-falu-hover">Integritet och personuppgifter</Link></li>
            <li><Link to="/villkor-inskickat" data-testid="ansvar-link-villkor" className="hover:text-falu-hover">Villkor för inskickat material</Link></li>
            <li><Link to="/ansvarig-utgivare" data-testid="ansvar-link-utgivare" className="hover:text-falu-hover">Ansvarig utgivare</Link></li>
          </ul>
        </section>

        {/* Regler */}
        <section className="bg-parchment border border-warm-border rounded-sm p-6 sm:p-8">
          <h2 className="font-heading text-xl sm:text-2xl font-semibold text-ink mb-3">Regler och riktlinjer</h2>
          <p className="text-sm text-ink/80 leading-relaxed mb-4">
            Vi vill att allt som publiceras ska vara hederligt, tydligt och respektfullt.
          </p>
          <Link to="/riktlinjer" data-testid="link-riktlinjer" className="text-sm font-semibold text-falu hover:text-falu-hover no-underline">
            Läs våra riktlinjer
          </Link>
        </section>

        {/* Kontakt */}
        <section className="bg-parchment border border-warm-border rounded-sm p-6 sm:p-8">
          <h2 className="font-heading text-xl font-semibold text-ink mb-2">Kontakta oss</h2>
          <p className="text-sm text-ink/80 mb-3">
            Har du frågor, förslag eller vill du hjälpa till?
          </p>
          <a href="mailto:redaktion@vidalv.live" data-testid="contact-email" className="text-sm font-semibold text-falu hover:text-falu-hover no-underline">
            redaktion@vidalv.live
          </a>
        </section>
      </div>
    </div>
  );
}
